import type { TFunction } from "i18next";

/**
 * BRIEF-CGNI slice 7 (trap 11) — the words for a budget refusal. The server RECORDS a refusal as a code and its parameters
 * (`budget-monthly-used {"payerKind":"person","payer":"Dana",…}`) in the column that used to hold the English sentence; this page writes
 * it in the reader's language. A row from before the change still holds the English sentence, and anything this file does not know is
 * shown exactly as it was stored — never guessed at.
 */

export type CodedSentence = { code: string; params: Record<string, string | number | null> };

const CODED = /^([a-z][a-z0-9]*(?:-[a-z0-9]+)*)(?: (\{[\s\S]*\}))?$/;

/** `code` or `code {"name":"value"}` → its parts; null for English and for anything that is not exactly that. */
export function parseCoded(text: string | null | undefined): CodedSentence | null {
  if (!text) return null;
  const match = CODED.exec(text);
  const code = match?.[1];
  if (!match || !code) return null;
  if (!match[2]) return { code, params: {} };
  try {
    const params: unknown = JSON.parse(match[2]);
    return params && typeof params === "object" && !Array.isArray(params)
      ? { code, params: params as CodedSentence["params"] }
      : null;
  } catch {
    return null;
  }
}

const text = (value: string | number | null | undefined) => (value == null ? "" : String(value));

const count = (lang: string, value: string | number | null | undefined) => {
  const n = Number(value);
  return Number.isFinite(n) ? new Intl.NumberFormat(lang).format(n) : text(value);
};

/** "2026-10" → "October 2026" / "אוקטובר 2026" */
const monthName = (lang: string, month: string | number | null | undefined) => {
  const [year, number] = text(month).split("-").map(Number);
  if (!year || !number) return text(month);
  return new Intl.DateTimeFormat(lang, { month: "long", year: "numeric", timeZone: "UTC" }).format(
    new Date(Date.UTC(year, number - 1, 1)),
  );
};

/** An ISO moment → "1 November" / "1 בנובמבר" (UTC: the allowance renews at 00:00 UTC). */
const dayName = (lang: string, iso: string | number | null | undefined) => {
  const at = new Date(text(iso));
  return Number.isNaN(at.getTime())
    ? text(iso)
    : new Intl.DateTimeFormat(lang, { day: "numeric", month: "long", timeZone: "UTC" }).format(at);
};

/** Who pays, in words: the kind and the project's or person's own name are content; the framing is ours. */
export function payerWords(t: TFunction, params: CodedSentence["params"]): string {
  const team = params.team ? text(params.team) : t("this workspace");
  switch (params.payerKind) {
    case "project":
      return t("Project {{name}}", { name: params.payer ? text(params.payer) : "?" });
    case "person":
      return t("{{name}}'s allowance in {{team}}", {
        name: params.payer ? text(params.payer) : t("This person"),
        team,
      });
    default:
      return t("Workspace {{team}}", { team });
  }
}

/** A coded budget refusal in the reader's language; null when `stored` is not one (so the caller shows the stored text). */
export function budgetWords(
  t: TFunction,
  stored: string | null | undefined,
  lang: string,
): string | null {
  const coded = parseCoded(stored);
  if (!coded) return null;
  const p = coded.params;
  switch (coded.code) {
    case "budget-no-allowance":
      return t("{{payer}} has no model allowance — an admin can give it one in Thinkers → Usage.", {
        payer: payerWords(t, p),
      });
    case "budget-monthly-used":
      return t(
        "{{payer}} has used {{used}} of its {{limit}} tokens for {{month}}; this call needs up to {{tokens}} — the allowance comes back on {{date}}.",
        {
          payer: payerWords(t, p),
          used: count(lang, p.used),
          limit: count(lang, p.limit),
          month: monthName(lang, p.month),
          tokens: count(lang, p.tokens),
          date: dayName(lang, p.resetsAt),
        },
      );
    case "budget-daily-used":
      return t(
        "{{payer}} has used {{used}} of its {{limit}} tokens for today; this call needs up to {{tokens}} — it comes back at 00:00 UTC.",
        {
          payer: payerWords(t, p),
          used: count(lang, p.used),
          limit: count(lang, p.limit),
          tokens: count(lang, p.tokens),
        },
      );
    case "budget-no-context":
      return t(
        "A model call ({{source}}) carries no funding context — the runner that started it has not said whose work it is.",
        { source: text(p.source) },
      );
    case "budget-foreign-funding":
      return t(
        "This call ({{source}}) names another workspace's budget ({{why}}) but runs on this workspace's thinker.",
        { source: text(p.source), why: text(p.why) },
      );
    default:
      return null;
  }
}
